import { useState, useRef, useEffect } from 'react'
import { ArrowRight, Send, AlertCircle, Cpu, MailCheck, BellOff, Check } from 'lucide-react'
import { useAssessment } from '../../context/AssessmentContext'
import Shell from '../ui/Shell'
import { pulse } from '../ui/LightField'
import { getProgress, STEP_LABEL } from '../../lib/flow'

// '' in production (nginx serves the app and proxies /api on the same origin);
// the dev server points at the local backend via .env.
const API_BASE = import.meta.env.VITE_API_URL ?? ''

const EMAIL_RE = /^\S+@\S+\.\S+$/

function EmailCollection() {
  const { state, dispatch } = useAssessment()
  const [email, setEmail] = useState(state.submittedEmail || '')
  const [error, setError] = useState('')
  const [shake, setShake] = useState(false)
  const [status, setStatus] = useState(state.resultsId ? 'sent' : 'idle') // idle | sending | sent | failed
  const inputRef = useRef(null)

  useEffect(() => {
    if (status === 'idle') inputRef.current?.focus()
  }, [status])

  const fail = (msg) => {
    setError(msg)
    setShake(true)
    window.setTimeout(() => setShake(false), 350)
    inputRef.current?.focus()
  }

  const submit = async (e) => {
    e.preventDefault()
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      fail('That doesn’t look like an email address.')
      return
    }
    setError('')
    setStatus('sending')

    try {
      const response = await fetch(`${API_BASE}/api/assessments/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: value,
          userAge: state.userAge,
          addictionResults: state.addictionResults,
          dassScores: state.dassScores,
          investorMetrics: state.investorMetrics,
          profileAnswers: state.profileAnswers || {},
          primaryAddiction: state.primaryAddiction,
          gates: state.gates,
          screenerAnswers: state.screenerAnswers,
          barc10Answers: state.barc10Answers,
          barc10Score: state.barc10Score,
        }),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(result.error || `Request failed (${response.status})`)
      }
      dispatch({ type: 'SET_RESULTS_ID', payload: result.data.resultsId })
      dispatch({ type: 'SET_SUBMITTED_EMAIL', payload: value })
      pulse(1.4, { x: 0.5, y: 0.35 })
      setStatus('sent')
    } catch (err) {
      console.error('Submit failed:', err)
      setStatus('failed')
      setError('We couldn’t send it. Check the connection and try again, or view the results here.')
    }
  }

  const viewResults = () => dispatch({ type: 'SET_SCREEN', payload: 'results' })

  return (
    <Shell
      label={STEP_LABEL.emailCollection}
      progress={getProgress(state)}
      onBack={status === 'sent' ? undefined : () => dispatch({ type: 'SET_SCREEN', payload: 'dassAssessment' })}
    >
      {status === 'sent' ? (
        <main className="flow">
          <div className="flow__head">
            <h1>Sent.</h1>
            <p className="flow__lede">Your readout is on its way. The link in the email keeps working, so you can come back to it later.</p>
          </div>
          <div className="sent">
            <span className="sent__badge"><MailCheck size={20} strokeWidth={2} /></span>
            <div>
              <p className="flow__lede flow__lede--sm">Delivered to</p>
              <p className="sent__email">{state.submittedEmail || email}</p>
            </div>
          </div>
          <div className="flow__actions">
            <button type="button" className="btn btn--block" onClick={viewResults}>
              View results now
              <ArrowRight size={18} strokeWidth={2.25} />
            </button>
          </div>
        </main>
      ) : (
        <form className="flow" onSubmit={submit} noValidate>
          <div className="flow__head">
            <h1>Where should we send your readout?</h1>
            <p className="flow__lede">Your scores are already calculated. The email carries a private link to the full page.</p>
          </div>

          <div>
            <label htmlFor="email" className="label">Email</label>
            <div className={shake ? 'shake' : ''}>
              <input
                ref={inputRef}
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? 'email-error' : undefined}
                onChange={(e) => { setEmail(e.target.value); if (error) setError('') }}
                disabled={status === 'sending'}
              />
            </div>
            {error && (
              <p id="email-error" className="error" role="alert">
                <AlertCircle size={16} strokeWidth={2} />
                {error}
              </p>
            )}
          </div>

          <ul className="what-happens" aria-label="What happens next">
            <li><Cpu size={14} strokeWidth={2} /><span>Scores were calculated on this device; only the summary is stored.</span></li>
            <li><MailCheck size={14} strokeWidth={2} /><span>One email, with a link to this readout.</span></li>
            <li><BellOff size={14} strokeWidth={2} /><span>No newsletter, no follow-ups unless you ask for them.</span></li>
          </ul>

          <div className="flow__actions">
            <button type="submit" className={`btn btn--block${status === 'sending' ? ' btn--loading' : ''}`} disabled={status === 'sending'}>
              {status === 'sending' ? <span className="btn__spinner" aria-hidden="true" /> : <Send size={18} strokeWidth={2.25} />}
              {status === 'sending' ? 'Sending' : 'Send my results'}
            </button>
            {status === 'failed' && (
              <button type="button" className="btn btn--secondary btn--block" onClick={viewResults}>
                <Check size={18} strokeWidth={2.25} />
                View results without sending
              </button>
            )}
          </div>
        </form>
      )}
    </Shell>
  )
}

export default EmailCollection
